import React, { useEffect, useRef, useState } from 'react';
import './skills.css';

const skillCards = [
  {
    num: '01',
    title: 'AI/ML & LLM Engineering',
    desc: 'Building multimodal and agentic AI systems using PyTorch, Hugging Face Transformers, and medical LLMs. Expertise in PEFT/LoRA fine-tuning, RAG pipelines, LangChain agent orchestration, tool calling, MCP-style interfaces, and structured JSON outputs.',
    tags: ['PyTorch', 'Hugging Face', 'LangChain', 'RAG', 'PEFT/LoRA', 'Multi-Agent Systems', 'CUDA', 'Gemini/GPT APIs'],
  },
  {
    num: '02',
    title: 'Cloud & Data Infrastructure',
    desc: 'AWS Certified Data Engineer with production experience in Snowflake, Amazon S3, Redshift, and AWS Glue. Skilled in Azure — EventHub (Kafka), Databricks Delta Live Tables, Delta Lake with Unity Catalog, and dbt Core Medallion Architecture.',
    tags: ['Snowflake', 'Amazon S3', 'Azure EventHub', 'Databricks', 'Delta Lake', 'dbt Core', 'Apache Airflow', 'AWS Glue'],
  },
  {
    num: '03',
    title: 'Backend & MLOps',
    desc: 'Building low-latency data APIs with FastAPI and Flask, managing ML experiments with MLflow, containerizing environments with Docker and Kubernetes, and automating deployments via GitHub Actions CI/CD.',
    tags: ['FastAPI', 'Flask', 'Docker', 'Kubernetes', 'GitHub Actions', 'MLflow', 'CI/CD', 'Pinecone'],
  },
];

const techItems = [
  'Python', 'SQL', 'PyTorch', 'Hugging Face', 'LangChain', 'RAG',
  'PEFT/LoRA', 'CUDA', 'dbt Core', 'Apache Airflow', 'Spark/PySpark',
  'Snowflake', 'Amazon S3', 'Redshift', 'AWS Glue', 'Azure EventHub',
  'Databricks', 'Delta Lake', 'FastAPI', 'Docker', 'Kubernetes',
  'MLflow', 'GitHub Actions', 'Streamlit', 'Tableau', 'Power BI', 'Pinecone',
];

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const SkillCard = ({ card, index }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`skillBar ${visible ? 'visible' : ''}`}
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      <span className="skillNum">{card.num}</span>
      <div className="skillBarText">
        <h2>{card.title}</h2>
        <p>{card.desc}</p>
        <div className="skillTags">
          {card.tags.map((tag, i) => (
            <span key={i} className="skillTag">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Skills = () => {
  const [headerRef, headerVisible] = useReveal(0.2);
  const [stackRef, stackVisible] = useReveal(0.2);

  return (
    <section id="skills">
      <div className="skillsInner">
        <div ref={headerRef} className={`skillsHeader ${headerVisible ? 'visible' : ''}`}>
          <span className="skillsLabel">EXPERTISE</span>
          <h2 className="skillTitle">What I Build</h2>
          <p className="skillDesc">
            AI Engineer and AWS Certified cloud practitioner with 1+ years building multimodal ML,
            LLM systems, and production data infrastructure across healthcare, manufacturing, and industry.
          </p>
        </div>

        <div className="skillBars">
          {skillCards.map((card, i) => (
            <SkillCard key={card.num} card={card} index={i} />
          ))}
        </div>

        <div ref={stackRef} className={`techStack ${stackVisible ? 'visible' : ''}`}>
          <span className="techStackLabel">
            <span className="termPromptGreen">$ </span>tech --list --all
          </span>
          <div className="techItems">
            {techItems.map((item, i) => (
              <span
                key={i}
                className="techItem"
                style={{ animationDelay: stackVisible ? `${i * 0.04}s` : '0s' }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;